import { Box, Link, Text, useToken } from '@chakra-ui/react'
import { useCallback, useContext } from 'react'
import { Link as RouterLink, useParams } from 'react-router'
import { stepsContext } from '../context/StepsContext'
import { useApp } from '../hooks/useApp'
import { StepsIconBeach, StepsIconBriefcase, StepsIconCar, StepsIconHouse } from '../icons'
import { insuranceParamsContext } from '../layouts/Wizard'

type IconType = 'car' | 'house' | 'briefcase' | 'beach'

interface StepProps {
  path?: string
  current?: boolean
  currentStep: number
  passed?: boolean
  icon: IconType
  number: number
  label: string
  totalSteps: number
}

interface StepsProps {
  icon: IconType
  /*stepsSet: 'carkulka' | 'carkulka-extra' | 'chaloupka' | 'sme' | 'cestovko'
  currentStep: number*/
}

interface StepLineProps {
  isFirst?: boolean
  isLast?: boolean
  passed?: boolean
  current?: boolean
}

const StepLine = ({ isFirst, isLast, passed, current }: StepLineProps) => (
  <Box
    sx={{
      width: '100%',
      height: 1,
      backgroundColor: passed || current ? 'secondary.50' : 'gray.200',
      visibility: isFirst || isLast ? 'hidden' : 'visible',
    }}
  />
)

const getStepIcon = ({ icon, iconColor }: { icon: IconType; iconColor: string }) => {
  switch (icon) {
    case 'car':
      return <StepsIconCar color={iconColor} />
    case 'house':
      return <StepsIconHouse color={iconColor} />
    case 'briefcase':
      return <StepsIconBriefcase color={iconColor} />
    case 'beach':
      return <StepsIconBeach color={iconColor} />
  }
}

const Step = ({ current, currentStep, passed, icon, number, label, path, totalSteps }: StepProps) => {
  const { insuranceParams } = useContext(insuranceParamsContext)

  const stepSize = 8
  const isFirst = number === 1
  const isLast = number === totalSteps
  const iconColor = useToken('colors', 'secondary.700')
  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const disabled = isContracted ? false : currentStep < number

  return (
    <Box as="li" sx={{ display: 'grid', gap: 1 }} className="steps__item">
      <Link
        onClick={(e) => {
          if (disabled)
            e.preventDefault()
        }}
        as={!disabled ? RouterLink : Box}
        {...(disabled ? {} : { to: path })}
        sx={{ display: 'grid', gap: 2, textDecoration: 'none', _hover: { textDecoration: 'none' }, cursor: disabled ? 'not-allowed' : 'pointer' }}
        className="steps__item-link"
      >
        <Box
          sx={{
            display: 'grid',
            alignItems: 'center',
            justifyItems: 'center',
            gridTemplateColumns: `auto ${useToken('sizes', 8)} auto`,
          }}
          className="steps__header"
        >
          <StepLine isFirst={isFirst} passed={passed} current={current} />

          <Box
            sx={{
              display: 'grid',
              alignContent: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              justifyItems: 'center',
              width: stepSize,
              height: stepSize,
              backgroundColor: current || passed ? 'secondary.50' : 'gray.200',
              borderRadius: 'full',
              color: 'primary.800',
              fontWeight: 'medium',
              textAlign: 'center',
              transitionProperty: 'all',
              transitionDuration: 'normal',
            }}
            className="steps__number"
          >
            {current && getStepIcon({ icon, iconColor })}

            {!current && number}
          </Box>

          <StepLine isLast={isLast} passed={passed} />
        </Box>

        <Text
          sx={{
            py: 0,
            px: 2,
            fontSize: { base: 'xs', md: 'md' },
            fontWeight: current ? 'medium' : 'initial',
            overflow: 'hidden',
            textAlign: 'center',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          className="steps__label"
        >
          {label}
        </Text>
      </Link>
    </Box>
  )
}

const steps: { [key: string]: { label: string; path: string }[] } = {
  carkulka: [
    {
      label: 'Základní údaje',
      path: '/carkulka/wizard/1',
    },
    {
      label: 'Připojištění a slevy',
      path: '/carkulka/wizard/2',
    },
    {
      label: 'Kalkulace',
      path: '/carkulka/wizard/3',
    },
    {
      label: 'Doplnění informací',
      path: '/carkulka/wizard/4',
    },
    {
      label: 'Shrnutí a sjednání',
      path: '/carkulka/wizard/5',
    },
  ],
  'carkulka-extra': [
    {
      label: 'Základní údaje',
      path: '/carkulka/extra/wizard/1',
    },
    {
      label: 'Kalkulace',
      path: '/carkulka/extra/wizard/2',
    },
    {
      label: 'Doplnění informací',
      path: '/carkulka/extra/wizard/3',
    },
    {
      label: 'Shrnutí a sjednání',
      path: '/carkulka/extra/wizard/4',
    },
  ],
  chaloupka: [
    {
      label: 'Základní údaje',
      path: '/chaloupka/wizard/1',
    },
    {
      label: 'Připojištění a slevy',
      path: '/chaloupka/wizard/2',
    },
    {
      label: 'Kalkulace',
      path: '/chaloupka/wizard/3',
    },
    {
      label: 'Doplnění informací',
      path: '/chaloupka/wizard/4',
    },
    {
      label: 'Shrnutí a sjednání',
      path: '/chaloupka/wizard/5',
    },
  ],
  sme: [
    {
      label: 'Základní údaje',
      path: '/sme/wizard/1',
    },
    {
      label: 'Připojištění a slevy',
      path: '/sme/wizard/2',
    },
    {
      label: 'Kalkulace',
      path: '/sme/wizard/3',
    },
    {
      label: 'Doplnění informací',
      path: '/sme/wizard/4',
    },
    {
      label: 'Shrnutí a sjednání',
      path: '/sme/wizard/5',
    },
  ],
  cestovko: [
    {
      label: 'Základní údaje',
      path: '/cestovko/wizard/1',
    },
    {
      label: 'Připojištění a slevy',
      path: '/cestovko/wizard/2',
    },
    {
      label: 'Kalkulace',
      path: '/cestovko/wizard/3',
    },
    {
      label: 'Informace o pojistníkovi',
      path: '/cestovko/wizard/4',
    },
    {
      label: 'Shrnutí a sjednání',
      path: '/cestovko/wizard/5',
    },
  ],
}

export const Steps = ({ icon }: StepsProps) => {
  const { app } = useApp()

  const { insuranceParamsId } = useParams()
  const { step: currentStep } = useContext(stepsContext)

  const getPath = useCallback((path: string) => {
    return insuranceParamsId ? path + '/' + insuranceParamsId : path
  }, [insuranceParamsId])

  const appSteps = steps[app!]

  return (
    <Box
      as="ul"
      id='steps'
      sx={{
        display: 'grid',
        gridAutoFlow: 'column',
        alignItems: 'center',
        justifyContent: 'stretch',
        listStyle: 'none',
      }}
    >
      {appSteps &&
        appSteps.map(({ label, path }, i) => (
          <Step
            key={label}
            path={getPath(path)}
            icon={icon}
            number={i + 1}
            label={label}
            currentStep={currentStep}
            current={currentStep === i + 1}
            passed={currentStep > i + 1}
            totalSteps={appSteps.length}
          />
        ))}
    </Box>
  )
}
